'use client';

import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { ProductListSeoFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListSeoFragmentFragmentDoc';
import type { SearchResultPageDefaultContentFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchResultPageDefaultContentFragmentFragmentDoc';
import type { CmsDataJsonApi } from '@packages/cms-components/interfaces';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import type { Device } from '@packages/shared/src/utils/types';

import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';
import { ProductListPageLayout } from '../ProductListPageLayout/ProductListPageLayout.client';
import { SearchResultHeader } from '../SearchResultHeader/SearchResultHeader';
import { SearchFeedback } from '../SearchFeedback/SearchFeedback.client';
import { ProductListSeo } from '../ProductListSeo/ProductListSeo';
import { SearchResultPageRecommendations } from '../SearchResultPageRecommendations/SearchResultPageRecommendations';
import { SearchResultCategoryFilter } from '../SearchResultCategoryFilter/SearchResultCategoryFilter';
import { CmsTopBanner } from '../CmsTopBanner/CmsTopBanner';
import { SearchKimRating } from '../SearchKimRating/SearchKimRating';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { bypassProductListPageLayoutMessages } from '../../bypassBlocks.messages';

/* GraphQL */ `
  fragment SearchResultPageDefaultContentFragment on SuccessfulSearchResult {
    ...SearchResultHeaderFragment
    ...ProductListPageLayoutFragment
    ...SearchResultCategoryFilterFragment
    ...SearchKimRatingFragment
    ...UseDisplayModeFragment
  }
`;
const messages = defineMessages({
  categoryFiltersLabel: {
    id: 'serp.categories.filters.label',
    defaultMessage: 'Kategorienfilter',
  },
});

export type SearchResultPageDefaultContentProps = {
  maskedData: FragmentType<typeof SearchResultPageDefaultContentFragmentFragmentDoc>;
  topLevelCategories?: string[];
  maskedSeoResponse?: FragmentType<typeof ProductListSeoFragmentFragmentDoc>;
  bannerData?: CmsDataJsonApi;
  device: Device;
  referrer?: string;
};

/**
 * SearchResultPageDefaultContent component description displayed in storybook
 * */
export const SearchResultPageDefaultContent: FC<SearchResultPageDefaultContentProps> = ({
  maskedData,
  topLevelCategories,
  maskedSeoResponse,
  bannerData,
  device,
  referrer,
}) => {
  const { searchResultUserFeedback } = useConfig();
  const originalQuery = useOriginalSearchQuery();
  const { formatMessage } = useIntl();

  const data = unmask<typeof SearchResultPageDefaultContentFragmentFragmentDoc>(maskedData);

  const { displayMode } = useDisplayMode(data);

  return (
    <ProductListPageLayout
      leftSidebarAriaLabel={formatMessage(messages.categoryFiltersLabel)}
      maskedData={data}
      topLevelCategories={topLevelCategories}
      device={device}
      referrer={referrer}
      enablePRankTracking={false}
      aboveMainContentComponent={
        <>
          {bannerData && <CmsTopBanner bannerData={bannerData} />}
          <SearchResultHeader maskedData={data} originalQuery={originalQuery} />
        </>
      }
      belowProductsComponent={
        <>
          <SearchResultPageRecommendations />
          {searchResultUserFeedback.enabled && <SearchFeedback />}
          <ErrorBoundary>
            <SearchKimRating
              maskedData={data}
              searchPageType="SERP"
              displayMode={displayMode}
              templateParams={{ x17: referrer ?? 'x' }}
            />
          </ErrorBoundary>
          {maskedSeoResponse && <ProductListSeo maskedData={maskedSeoResponse} />}
        </>
      }
      leftSidebarDesktopComponent={
        <BypassBlock
          buttonText={formatMessage(bypassProductListPageLayoutMessages.categoryNavigation)}
          skipScrollIntoView
        >
          <SearchResultCategoryFilter maskedData={data} />
        </BypassBlock>
      }
      renderCategoryDialogContent={(closeDialog) => (
        <SearchResultCategoryFilter maskedData={data} onNavigate={closeDialog} />
      )}
    />
  );
};
